import "./styles.scss";
import PropTypes from "prop-types";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { ActionBar, Tracks, PlayPauseLogo } from "./widgets";

import {
  useHandleAspectRatio,
  useHandleHLSVideo,
  useManageControl,
} from "./hooks";

import { useElementInScreen } from "hooks";

const DEFAULT_BUTTONS_CONFIG = {
  play_pause: true,
  time: true,
  progress_bar: true,
  fullScreen: true,
  mute: true,
};

export const ITXVideo = forwardRef((props, componentRef) => {
  const {
    source,
    sourceWeb,
    source2K,
    source4K,
    poster,
    videoMode = "center",
    caption,
    loop = true,
    autoPlay = false,
    muted = false,

    actionAnimation = true,
    hideActionBar = false,
    className = "",
    containerClassName = "",
    locale = "en",
    buttons = {},
    autoHideTime = 0,
    useAspectRatio = false,
  } = props;
  const defaultProps = {
    loop: true,
    autoPlay: false,
    muted: false,
    allowPlayPause: true,
    allowFullscreen: true,
    actionAnimation: true,
    hideActionBar: false,
    useAspectRatio: false,
    autoHideTime: 0,
    buttons: {},
  };
  const [showElement, setShowElement] = useState(false);

  const videoRef = useRef(null);
  const wrapperRef = useRef(null);
  const actionBarRef = useRef(null);
  const [loadMedia, setLoadMedia] = useState(false);
  const { isInViewPort } = useElementInScreen(wrapperRef, "15%");
  const { isInViewPort: videoInViewPort } = useElementInScreen(wrapperRef);
  useEffect(() => {
    if (isInViewPort && !showElement) {
      setShowElement(true);
    }
  }, [isInViewPort]); //eslint-disable-line

  useEffect(() => {
    // If autoplay is false, we don't want to play the video
    if (!autoPlay) return;

    if (videoInViewPort && videoRef.current) {
      (async () => {
        try {
          await videoRef.current.play();
        } catch (err) {
          console.warn("Autoplay play() interrupted:", err);
        }
      })();
    } else if (!videoInViewPort && videoRef.current) {
      videoRef.current.pause();
    }
  }, [videoInViewPort, autoPlay, videoRef]);

  useEffect(() => {
    setLoadMedia(true);
  }, []); //eslint-disable-line
  const { playingStatus, hideBar, isFullScreen, setIsFullScreen, handleClick } =
    useManageControl(videoRef, actionBarRef, wrapperRef, {
      ...defaultProps,
      ...props,
    });
  const { isHlsTrack, currentVideo, isIphone, hlsReady, videoType } =
    useHandleHLSVideo(loadMedia, videoRef, {
      source,
      sourceWeb,
      source2K,
      source4K,
    });

  const { containerStyle } = useHandleAspectRatio(
    { ...defaultProps, ...props },
    useAspectRatio
  );
  const buttonsConfig = { ...DEFAULT_BUTTONS_CONFIG, ...buttons };
  /* c8 ignore next 27 */
  useImperativeHandle(componentRef, () => ({
    videoRef,
    play: async () => {
      if (videoRef.current) await videoRef.current.play();
    },
    pause: () => {
      if (videoRef.current) videoRef.current.pause();
    },
    mute: (value) => {
      if (videoRef.current)
        videoRef.current.muted =
          value !== undefined ? value : !videoRef.current.muted;
    },
    toggleFullScreen: () => {
      if (videoRef.current) videoRef.current.requestFullscreen();
    },
    replay: async () => {
      if (videoRef.current) {
        videoRef.current.currentTime = 0;
        try {
          await videoRef.current.play();
        } catch (err) {
          console.warn("Replay play() interrupted:", err);
        }
      }
    },
    seek: (time) => {
      if (videoRef.current) videoRef.current.currentTime = time;
    },
    playingStatus,
  }));
  const renderSrc = () => {
    if (isIphone) {
      return currentVideo;
    }
    if (isHlsTrack(currentVideo)) {
      return currentVideo;
    }
    return undefined;
  };

  return (
    <div
      ref={wrapperRef}
      className={`ITXVideo ${videoMode} ${containerClassName} ${
        isFullScreen ? "fullScreen" : ""
      } ${isIphone && isFullScreen ? "fullScreen-iphone" : ""}`}
      style={containerStyle}
    >
      {hlsReady && showElement ? (
        <>
          <video
            playsInline={true}
            controls={false}
            ref={videoRef}
            className={className}
            loop={loop}
            src={renderSrc()}
            muted={muted}
            preload="metadata"
            onClick={handleClick}
          >
            {poster && (
              <img
                width={1920}
                height={1080}
                src={poster}
                alt="poster"
                className="ITXVideo__poster"
              />
            )}
            {!isHlsTrack(currentVideo) && (
              <source
                key={"main"}
                data-testid={"main"}
                src={currentVideo}
                type={videoType}
              />
            )}

            <Tracks
              source={source}
              sourceWeb={sourceWeb}
              source2K={source2K}
              source4K={source4K}
              caption={caption}
            />
          </video>
          {actionAnimation && videoRef.current && (
            <PlayPauseLogo playingStatus={playingStatus} />
          )}
          {!hideActionBar && videoRef.current && (
            <ActionBar
              hideBar={hideBar}
              isFullScreen={isFullScreen}
              setIsFullScreen={setIsFullScreen}
              autoHideTime={autoHideTime}
              wrapperRef={wrapperRef.current}
              videoRef={videoRef.current}
              locale={locale}
              playingStatus={playingStatus}
              buttons={buttonsConfig}
            />
          )}
        </>
      ) : null}
    </div>
  );
});

ITXVideo.propTypes = {
  source: PropTypes.string.isRequired,
  sourceWeb: PropTypes.string,
  source2K: PropTypes.string,
  source4K: PropTypes.string,
  caption: PropTypes.string,
  loop: PropTypes.bool,
  autoPlay: PropTypes.bool,
  muted: PropTypes.bool,

  containerClassName: PropTypes.string,
  actionAnimation: PropTypes.bool,
  className: PropTypes.string,
  allowPlayPause: PropTypes.bool,
  allowFullscreen: PropTypes.bool,
  locale: PropTypes.string.isRequired,
  hideActionBar: PropTypes.bool,
  videoMode: PropTypes.oneOf(["center", "adjust"]),
  poster: PropTypes.string,

  buttons: PropTypes.shape({
    play_pause: PropTypes.bool,
    time: PropTypes.bool,
    progress_bar: PropTypes.bool,
    fullScreen: PropTypes.bool,
    mute: PropTypes.bool,
  }),

  autoHideTime: PropTypes.number,
};
